import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Clock, RefreshCw, Trash2, CheckCircle2, Copy, MessageCircle, X } from 'lucide-react';
import api from '../../services/api';
import { useToastContext } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';

/**
 * Sport dashboard: intake links waiting for the client, plus the last few
 * completed ones. Resend mints a fresh link (the old one stops working — only
 * a hash of the token is stored, so an old link can't be shown again).
 */
/** 972501234567 -> 050-123-4567, the way managers read numbers. */
function localPhone(p) {
  const d = String(p || '').replace(/\D/g, '');
  const local = d.startsWith('972') ? `0${d.slice(3)}` : d;
  return local.length === 10 ? `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}` : local;
}

export default function PendingIntakesCard({ intakes = [], onChanged }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { success, error: toastError } = useToastContext();
  const confirm = useConfirm();
  const [busyId, setBusyId] = useState(null);
  // A resend WhatsApp couldn't deliver: the new link (the old one is dead now,
  // and only a hash of it is stored) stays on screen until the manager shares it.
  const [fallback, setFallback] = useState(null);

  const [clearing, setClearing] = useState(false);
  const pending = intakes.filter((i) => i.status === 'pending');
  const expiredCount = pending.filter((i) => i.expired).length;
  // Recently signed — minus ones the manager hid and clients deleted since.
  const recentDone = intakes
    .filter((i) => i.status === 'completed' && !i.dismissed && i.studentActive !== false)
    .slice(0, 3);
  if (!pending.length && !recentDone.length) return null;

  const when = (iso) => {
    if (!iso) return '';
    try {
      return new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
    } catch { return iso; }
  };
  const name = (i) => `${i.firstName || ''} ${i.lastName || ''}`.trim();

  const resend = async (intake) => {
    setBusyId(intake.id);
    try {
      const res = await api.post(`/client-intakes/${intake.id}/resend`);
      const data = res?.data || res;
      if (data?.whatsapp?.sent) {
        success(t('sport.intake.resent'));
        setFallback(null);
      } else {
        setFallback({ id: intake.id, name: name(intake) || localPhone(intake.phone), url: data.url, messageText: data.messageText, phone: data.phone || intake.phone });
        toastError(t('sport.intake.resendNotSent'));
      }
      onChanged?.();
    } catch (err) {
      toastError(err?.response?.data?.message || err?.message || t('common.error'));
    } finally {
      setBusyId(null);
    }
  };

  // Runs inside the click, so the clipboard write is allowed on every browser.
  const copyFallback = async () => {
    try {
      await navigator.clipboard.writeText(fallback.url);
      success(t('sport.intake.copied'));
    } catch {
      toastError(t('sport.intake.copyFailed'));
    }
  };

  const cancel = async (intake) => {
    const ok = await confirm({
      title: t('sport.intake.cancelTitle'),
      message: t('sport.intake.cancelMessage'),
      confirmText: t('sport.intake.cancelConfirm'),
      isDangerous: true,
    });
    if (!ok) return;
    setBusyId(intake.id);
    try {
      await api.delete(`/client-intakes/${intake.id}`);
      onChanged?.();
    } catch (err) {
      toastError(err?.response?.data?.message || err?.message || t('common.error'));
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (intake) => {
    setBusyId(intake.id);
    try {
      await api.post(`/client-intakes/${intake.id}/dismiss`);
      onChanged?.();
    } catch (err) {
      toastError(err?.response?.data?.message || err?.message || t('common.error'));
    } finally {
      setBusyId(null);
    }
  };

  const clearExpired = async () => {
    const ok = await confirm({
      title: t('sport.intake.deleteExpiredTitle', { count: expiredCount }),
      message: t('sport.intake.cancelMessage'),
      confirmText: t('sport.intake.delete'),
      isDangerous: true,
    });
    if (!ok) return;
    setClearing(true);
    try {
      await api.post('/client-intakes/cancel-expired');
      onChanged?.();
    } catch (err) {
      toastError(err?.response?.data?.message || err?.message || t('common.error'));
    } finally {
      setClearing(false);
    }
  };

  const deleteBtn = {
    minHeight: 44, padding: '0 12px', flexShrink: 0, boxShadow: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
    borderRadius: 10, border: '1px solid var(--danger-600, #dc2626)', background: 'var(--color-surface, #fff)',
    color: 'var(--danger-600, #dc2626)', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
  };
  const iconBtn = {
    width: 44, height: 44, minWidth: 44, minHeight: 44, padding: 0, flexShrink: 0, boxShadow: 'none',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 10, border: '1px solid var(--color-border, #e5e7eb)', background: 'var(--color-surface, #fff)',
    cursor: 'pointer', color: 'var(--color-text-secondary)',
  };

  return (
    <section className="sport-intakes" data-testid="sport-pending-intakes">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 className="learning-dashboard__section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginInlineEnd: 'auto' }}>
          <Clock size={18} /> {t('sport.intake.pendingTitle', { count: pending.length })}
        </h2>
        {expiredCount > 0 && (
          <button type="button" style={deleteBtn} disabled={clearing} onClick={clearExpired} data-testid="sport-intakes-delete-expired">
            <Trash2 size={16} /> {t('sport.intake.deleteExpired', { count: expiredCount })}
          </button>
        )}
      </div>
      {fallback && (
        <div
          data-testid="sport-intake-fallback"
          style={{ display: 'grid', gap: 8, padding: '0.75rem', marginBottom: '0.75rem', borderRadius: 12, border: '1px solid var(--warning-600, #d97706)', background: 'var(--warning-50, #fffbeb)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ flex: 1, minWidth: 0, fontSize: '0.9rem' }}>{t('sport.intake.newLinkFor', { name: fallback.name })}</strong>
            <button type="button" style={iconBtn} onClick={() => setFallback(null)} aria-label={t('common.close')}><X size={18} /></button>
          </div>
          <input readOnly value={fallback.url} dir="ltr" onFocus={(e) => e.target.select()} style={{ width: '100%', minHeight: 44, padding: '0 0.6rem', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 16, boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text-primary)' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
            <button type="button" className="btn btn-secondary" style={{ minHeight: 44, display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'center' }} onClick={copyFallback}>
              <Copy size={16} /> {t('sport.intake.copy')}
            </button>
            <a className="btn btn-secondary" href={`https://wa.me/${String(fallback.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(fallback.messageText || fallback.url)}`} target="_blank" rel="noreferrer" style={{ minHeight: 44, display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
              <MessageCircle size={16} /> {t('sport.intake.openWhatsApp')}
            </a>
          </div>
        </div>
      )}
      <ul className="sport-intakes__list">
        {pending.map((i) => (
          <li key={i.id} className="sport-intakes__item" data-testid="sport-pending-intake">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name(i) || <bdi dir="ltr">{localPhone(i.phone)}</bdi>}
              </div>
              <div style={{ fontSize: '0.8rem', color: i.expired ? 'var(--danger-600, #dc2626)' : 'var(--color-text-tertiary)' }}>
                {name(i) && <><bdi dir="ltr">{localPhone(i.phone)}</bdi> · </>}
                {i.expired
                  ? t('sport.intake.expired')
                  : i.lastSend && i.lastSend.sent === false
                    ? t('sport.intake.notDelivered')
                    : t('sport.intake.sentAt', { when: when(i.lastSentAt || i.createdAt) })}
              </div>
            </div>
            <button type="button" style={iconBtn} disabled={busyId === i.id} onClick={() => resend(i)} aria-label={t('sport.intake.resend')} title={t('sport.intake.resend')}>
              {i.lastSend && i.lastSend.sent === false ? <Copy size={18} /> : <RefreshCw size={18} />}
            </button>
            <button type="button" style={deleteBtn} disabled={busyId === i.id} onClick={() => cancel(i)} aria-label={t('sport.intake.cancel')} title={t('sport.intake.cancel')} data-testid="sport-intake-delete">
              <Trash2 size={16} /> <span>{t('sport.intake.delete')}</span>
            </button>
          </li>
        ))}
        {recentDone.map((i) => (
          <li key={i.id} className="sport-intakes__item sport-intakes__item--done">
            <CheckCircle2 size={18} style={{ color: 'var(--success-600, #059669)', flexShrink: 0 }} />
            <button
              type="button"
              onClick={() => i.studentId && navigate(`/students/${i.studentId}`)}
              style={{ flex: 1, minWidth: 0, textAlign: 'start', background: 'none', border: 'none', boxShadow: 'none', cursor: 'pointer', color: 'var(--color-text-primary)', minHeight: 44, padding: 0, display: 'block' }}
            >
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name(i) || <bdi dir="ltr">{localPhone(i.phone)}</bdi>}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>{t('sport.intake.signedAt', { when: when(i.submittedAt) })}</div>
            </button>
            <button type="button" style={iconBtn} disabled={busyId === i.id} onClick={() => dismiss(i)} aria-label={t('sport.intake.hideSigned')} title={t('sport.intake.hideSigned')} data-testid="sport-intake-dismiss">
              <X size={18} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
